import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { getGeolocationCookieName } from '../getters/cookieGetters';
import { Logger } from '../logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '../logger/datadog/types';
import { useStore } from '@/store/useStore';
import { Region } from '@/store/slices/session/types';
import { removeCookieEverywhere } from './removeCookieEverywhere';

export type RequiredGeolocationProduct = 'CASINO' | 'POKER' | 'SB';

const extractGeolocationToken = (
  requiredProduct?: RequiredGeolocationProduct,
) => {
  const currentSessionId = useStore.getState().userSessionContext.sessionId;
  const runtimeRegion = useStore.getState().runtime.region.toLocaleUpperCase();

  const geocomplyCookieName = getGeolocationCookieName();
  const cookieCachedGeotoken = Cookies.get(geocomplyCookieName);
  try {
    if (!cookieCachedGeotoken) return '';

    const { expires, session_id, state, product, products } = jwtDecode<{
      expires: string;
      session_id: number;
      state: Region;
      product: 'CASINO' | 'POKER';
      products: Array<'CASINO' | 'POKER' | 'SB'>;
    }>(cookieCachedGeotoken);

    const geotokenExpiry = new Date(expires).getTime();

    const isGeotokenValid =
      Number.isFinite(geotokenExpiry) && Date.now() < geotokenExpiry;

    const doesGeotokenMatchSession = session_id === Number(currentSessionId);

    const doesGeotokenMatchState = state === runtimeRegion;

    const doesGeotokenMatchProduct =
      !requiredProduct ||
      products?.includes(requiredProduct) ||
      product === requiredProduct;

    if (!doesGeotokenMatchProduct) {
      Logger.logRumError({
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        name: '[GW][GEOLOCATION] Cached geotoken does not match current product',
        context: {
          product,
          products,
          requiredProduct,
        },
      });
      removeCookieEverywhere(geocomplyCookieName);
      return '';
    }

    if (!doesGeotokenMatchState) {
      Logger.logRumError({
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        name: '[GW][GEOLOCATION] Cached geotoken does not match current state',
        context: {
          state,
          runtimeRegion,
        },
      });
      removeCookieEverywhere(geocomplyCookieName);

      return '';
    }

    if (!doesGeotokenMatchSession) {
      Logger.logRumError({
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        name: '[GW][GEOLOCATION] Cached geotoken does not match current session_id',
        context: {
          session_id,
          currentSessionId,
        },
      });
      removeCookieEverywhere(geocomplyCookieName);

      return '';
    }
    if (!isGeotokenValid) {
      Logger.logRumError({
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        name: '[GW][GEOLOCATION] Geotoken expired',
        context: {
          session_id,
          currentSessionId,
          geotokenExpiry,
        },
      });
      removeCookieEverywhere(geocomplyCookieName);

      return '';
    }

    return cookieCachedGeotoken;
  } catch (_error: unknown) {
    Logger.logRumError({
      useCase: DdEventCategory.Initialization,
      eventType: DdEventType.Error,
      name: '[GW][GEOLOCATION] Failed to parse geotoken',
      context: {
        error: _error as Error,
      },
    });
    return '';
  }
};

export default extractGeolocationToken;
